import { renameObjectFields, updateField } from "../helpers/helpers";
import { store } from "@services/redux/store";
import { getRoute, apiSend } from "@services/axios";

// ====================================
// MAIN API FUNCTION FOR SUBMISSION SYSTEM
// ====================================

/**
 * Main function to handle transport product submissions based on level
 * This function will be used by the submission helpers
 * @param {Object} productData - Product data for the current step
 * @param {number} level - API level (1, 2, or 3)
 * @returns {Promise} API response
 */
export const postTransport = async (productId = null, productData, level) => {
  console.log(`🚀 postTransport called for level ${level}`);
  switch (level) {
    case 1:
      return await postTransferStep1(productData, level);

    case 2:
      // For level 2, we need to handle both details and images
      return await postTransferStep2(productId, productData, level);

    case 3:
      return await postTransferStep3(productId, productData, level);

    default:
      throw new Error(`Unsupported level: ${level} for transport product`);
  }
};

// Export the main function for use in submission helpers
export default postTransport;

// ====================================
// STEP 1 - CREATE TRANSPORT PRODUCT
// ====================================

export const postTransferStep1 = async (productData, level) => {
  const data = renameObjectFields(productData, {
    transport_type_id: "product_type_id",
  });

  try {
    const url = `${getRoute("create_transfer_step_1")}`;

    const response = await apiSend(url, "post", data);
    console.log(response);
    const result = response.data.result;

    if (result?.product_id) {
      store.dispatch({
        type: "transport/setTransportProductId",
        payload: result.product_id,
      });
    }

    localStorage.setItem("product_id_save", result.product_id);

    if (result?.product_transfer?.length) {
      // ✅ Update Redux
      result.product_transfer.forEach((realId, idx) => {
        store.dispatch({
          type: "transport/updateTransportStepField",
          payload: {
            field: `step3.pricing.${idx}.product_transfer`,
            value: realId,
          },
        });
      });

      store.dispatch({
        type: "transport/updateTransportStepField",
        payload: {
          field: `step2.transfer_ids`,
          value: result.product_transfer,
        },
      });

      // Also patch productData directly (so Step 3 has real IDs immediately)
      result.product_transfer.forEach((realId, idx) => {
        if (productData.step3?.pricing?.[idx]) {
          productData.step3.pricing[idx].product_transfer_id = realId;
        }
      });
    }

    return result;
  } catch (error) {
    console.error("Error posting transfer step 1:", error);
    throw error;
  }
};

// ====================================
// STEP 2 - UPDATE TRANSFER DETAILS + VEHICLES
// ====================================

export const postTransferStep2 = async (product_id, productData, level) => {
  const productType = "Transport";
  productData = store.getState()[productType.toLowerCase()]?.step2;

  const transfers = productData.transfer_ids.map((transferId) => ({
    trasnfer_id: String(transferId), // backend expects string (typo preserved)
    short_summary: productData.short_summary,
    description_detailles: productData.detailed_description,
    strong_point: productData.key_points,
    key_word: productData.keyword,
    cars: productData.cars.map((car) => ({
      car_type_id: car.car_type_id,
      car_model_id: car.car_model_id,
      car_brand_id: car.car_brand_id,
      capacity_max: car.capacity_max,
      waiting_time: car.waiting_time,
      luggage_capacity: car.luggage_capacity,
      equipment_ids: car.equipment_ids,
    })),
  }));

  const data = { transfers };
  console.log("🚀 Submitting Step 2 with data:", data);

  try {
    const url = `${getRoute("create_transfer_step_2")}/${product_id}`;

    const response = await apiSend(url, "post", data);

    const result = response.data?.result;
    console.log("✅ Step 2 response:", result);

    const productTransfers = result?.product_transfer;
    console.log("Product Transfers from backend:", productTransfers);

    if (!productTransfers || typeof productTransfers !== "object") {
      console.warn("⚠️ No valid product_transfer data returned from step 2");
      return response.data;
    }

    Object.entries(productTransfers).forEach(([transferId, data], idx) => {
      console.log(transferId, data, idx);
      // 1️⃣ Set product_transfer_id
      updateField(
        store.dispatch,
        `step3.pricing.${idx}.product_transfer_id`,
        Number(transferId)
      );

      // 2️⃣ Set car_ids
      if (Array.isArray(data.vehicle)) {
        data.vehicle.forEach((carId, carIdx) => {
          updateField(
            store.dispatch,
            `step3.pricing.${idx}.prices.${carIdx}.car_id`,
            carId
          );
        });
      }
    });

    console.log(
      "✅ Updated Redux with product_transfer_id + car_id from backend"
    );

    return response.data;
  } catch (error) {
    console.error("Error posting transfer step 2 details:", error);
    throw error;
  }
};

// ====================================
// STEP 3 - FINAL PRICING + POLICIES
// ====================================

export const postTransferStep3 = async (product_id, productData, level) => {
  try {
    const productType = "Transport";
    productData = store.getState()[productType.toLowerCase()]?.step3;

    console.log("🚀 Starting postTransferStep3 with data:", productData);

    if (!productData.pricing || !Array.isArray(productData.pricing)) {
      throw new Error("pricing array is missing for Step 3");
    }

    // ✅ Transform Redux structure → Backend structure
    const transfers = productData.pricing.map((transfer) => ({
      trasnfer_id: String(transfer.product_transfer_id), // backend expects string (typo preserved)
      start_date: transfer.start_date || "2026-01-01", // TODO: replace with real state fields
      end_date: transfer.end_date || "2026-12-31",
      cars: transfer.prices.map((price) => ({
        id: price.car_id,
        day_price: price.day_price,
        night_price: price.night_price,
        is_place: price.is_place,
      })),
    }));

    const payload = {
      cancellation_policy_id: productData.cancellation_policy_id,
      cancellation_policy_percentage:
        productData.cancellation_policy_percentage || "5",
      booking_confirmation_mode: productData.booking_confirmation_mode,
      charge_service: localStorage.getItem("charge_service"),
      charge_service_type: localStorage.getItem("charge_service_type"),
      transfers,
    };

    console.log("✅ Final Step 3 payload:", payload);

    const url = `${getRoute("create_transfer_step_3")}/${product_id}`;
    const response = await apiSend(url, "post", payload);
    console.log("response 3 :", response);
    return response?.data?.result;
  } catch (error) {
    console.error("Error posting transfer step 3:", error);
    throw error;
  }
};
